import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BankAccountForm = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    accountType: 'bank',
    accountName: '',
    accountCode: '',
    currency: 'USD',
    accountNumber: '',
    bankName: '',
    bankIdentifierCode: '',
    description: '',
    isPrimary: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currencies = [
    { value: 'USD', label: 'US Dollar (USD)' },
    { value: 'EUR', label: 'Euro (EUR)' },
    { value: 'GBP', label: 'British Pound (GBP)' },
    { value: 'CAD', label: 'Canadian Dollar (CAD)' },
    { value: 'AUD', label: 'Australian Dollar (AUD)' },
    { value: 'JPY', label: 'Japanese Yen (JPY)' },
    { value: 'CHF', label: 'Swiss Franc (CHF)' },
    { value: 'CNY', label: 'Chinese Yuan (CNY)' },
    { value: 'INR', label: 'Indian Rupee (INR)' }
  ];

  const accountTypes = [
    { value: 'bank', label: 'Bank Account' },
    { value: 'credit_card', label: 'Credit Card' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.accountName?.trim()) {
      newErrors.accountName = 'Account name is required';
    }

    if (!formData?.accountNumber?.trim()) {
      newErrors.accountNumber = 'Account number is required';
    }

    if (!formData?.bankName?.trim()) {
      newErrors.bankName = formData?.accountType === 'bank' ? 'Bank name is required' : 'Credit card company is required';
    }

    if (formData?.description?.length > 500) {
      newErrors.description = 'Description must not exceed 500 characters';
    }

    // Account code validation (alphanumeric) - only if provided
    if (formData?.accountCode && !/^[a-zA-Z0-9]+$/?.test(formData?.accountCode)) {
      newErrors.accountCode = 'Account code must contain only letters and numbers';
    }

    // Account number validation based on type
    if (formData?.accountType === 'bank' && formData?.accountNumber && formData?.accountNumber?.length < 6) {
      newErrors.accountNumber = 'Bank account number must be at least 6 digits';
    }

    if (formData?.accountType === 'credit_card' && formData?.accountNumber) {
      const cleanNumber = formData?.accountNumber?.replace(/\s/g, '');
      if (!/^\d{13,19}$/?.test(cleanNumber)) {
        newErrors.accountNumber = 'Credit card number must be between 13-19 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the onSubmit prop to handle the actual database operation
      await onSubmit?.(formData);
      
      // Reset form on success
      handleReset();
    } catch (error) {
      console.error('Error creating account:', error);
      // Error handling is done in the parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      accountType: 'bank',
      accountName: '',
      accountCode: '',
      currency: 'USD',
      accountNumber: '',
      bankName: '',
      bankIdentifierCode: '',
      description: '',
      isPrimary: false
    });
    setErrors({});
  };

  const formatAccountNumber = (value, type) => {
    if (type === 'credit_card') {
      // Format credit card number with spaces
      const cleaned = value?.replace(/\s/g, '');
      const formatted = cleaned?.replace(/(.{4})/g, '$1 ');
      return formatted?.trim();
    }
    return value;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-foreground">Add Bank or Credit Card</h2>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
            />
          </div>
          <p className="text-muted-foreground mt-2">
            Add your bank account or credit card details manually
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Account Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-3">
              Select Account Type <span className="text-error">*</span>
            </label>
            <div className="flex gap-4">
              {accountTypes?.map(type => (
                <div key={type?.value} className="flex-1">
                  <label className="flex items-center p-4 border border-border rounded-lg cursor-pointer hover:bg-muted/20 transition-colors">
                    <input
                      type="radio"
                      name="accountType"
                      value={type?.value}
                      checked={formData?.accountType === type?.value}
                      onChange={(e) => handleChange('accountType', e?.target?.value)}
                      className="w-4 h-4 text-blue-600 border-border focus:ring-blue-500 mr-3"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground">{type?.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {type?.value === 'bank' ? 'Checking, Savings, Business accounts' : 'Credit cards, Corporate cards'}
                      </p>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Account Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Account Name <span className="text-error">*</span>
              </label>
              <Input
                type="text"
                value={formData?.accountName}
                onChange={(e) => handleChange('accountName', e?.target?.value)}
                placeholder={formData?.accountType === 'bank' ? 'e.g., Business Checking' : 'e.g., Business Credit Card'}
                error={errors?.accountName}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A descriptive name for your account
              </p>
            </div>

            {/* Account Code */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Account Code
              </label>
              <Input
                type="text"
                value={formData?.accountCode}
                onChange={(e) => handleChange('accountCode', e?.target?.value?.toUpperCase())}
                placeholder="e.g., CHK001 (Optional)"
                error={errors?.accountCode}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Unique code for internal reference (auto-generated if empty)
              </p>
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Currency <span className="text-error">*</span>
              </label>
              <Select
                value={formData?.currency}
                onChange={(value) => handleChange('currency', value)}
                options={currencies}
                placeholder="Select currency"
                className="w-full"
              />
            </div>

            {/* Account Number */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Account Number <span className="text-error">*</span>
              </label>
              <Input
                type="text"
                value={formData?.accountNumber}
                onChange={(e) => {
                  const formatted = formatAccountNumber(e?.target?.value, formData?.accountType);
                  handleChange('accountNumber', formatted);
                }}
                placeholder={formData?.accountType === 'bank' ? 'Enter bank account number' : 'Enter credit card number'}
                error={errors?.accountNumber}
                className="w-full"
                maxLength={formData?.accountType === 'credit_card' ? 23 : undefined}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData?.accountType === 'bank' ?'Your bank account number (will be masked in displays)' :'Your credit card number (will be masked for security)'
                }
              </p>
            </div>

            {/* Bank Name / Credit Card Company */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {formData?.accountType === 'bank' ? 'Bank Name' : 'Credit Card Company'} <span className="text-error">*</span>
              </label>
              <Input
                type="text"
                value={formData?.bankName}
                onChange={(e) => handleChange('bankName', e?.target?.value)}
                placeholder={formData?.accountType === 'bank' ? 'e.g., Chase Bank' : 'e.g., American Express'}
                error={errors?.bankName}
                className="w-full"
              />
            </div>

            {/* Bank Identifier Code */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {formData?.accountType === 'bank' ? 'Bank Identifier Code' : 'Card Network'}
              </label>
              <Input
                type="text"
                value={formData?.bankIdentifierCode}
                onChange={(e) => handleChange('bankIdentifierCode', e?.target?.value?.toUpperCase())}
                placeholder={formData?.accountType === 'bank' ? 'e.g., CHASUS33 (Optional)' : 'e.g., VISA, MASTERCARD'}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData?.accountType === 'bank' ?'SWIFT/BIC code for international transfers (optional)' :'Credit card network (Visa, MasterCard, etc.)'
                }
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              placeholder="Add a description for this account (optional)"
              rows={3}
              maxLength={500}
              className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-muted-foreground">
                Optional description for additional account details
              </p>
              <span className={`text-xs ${formData?.description?.length > 450 ? 'text-warning' : 'text-muted-foreground'}`}>
                {formData?.description?.length || 0}/500
              </span>
            </div>
            {errors?.description && (
              <p className="text-sm text-error mt-1">{errors?.description}</p>
            )}
          </div>

          {/* Make Primary */}
          <div className="mt-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData?.isPrimary}
                onChange={(e) => handleChange('isPrimary', e?.target?.checked)}
                className="w-4 h-4 text-blue-600 border-border rounded focus:ring-blue-500 mr-3"
              />
              <span className="text-sm font-medium text-foreground">Make this primary</span>
            </label>
            <p className="text-xs text-muted-foreground mt-1 ml-7">
              Set as the default account for transactions and payments
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                handleReset();
                onClose?.();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleReset}
              disabled={isSubmitting}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Plus"
              iconPosition="left"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding Account...' : 'Add Account'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankAccountForm;